import FailureTransationLoader from "@assets/lottie_icons/transaction_failure.json";
import InProgressTransactionLoader from "@assets/lottie_icons/transaction_in_progress.json";
import SuccessTransactionLoader from "@assets/lottie_icons/transaction_success.json";
import TransactionStatusClass from "@components/classes/TransactionStatusClass";
import GenericError from "@components/errors/GenericError";
// import { transactionService } from "@services/api/Transactions";
// import {
//   SettleTransactionRequest,
//   SettleTransactionResponse,
// } from "@services/models/api_models/transaction/SettleTransactionModel";
import { TransactionByIdResponseData } from "@services/models/Transactions/TransactionByIdModel";
import webSocketService from "@services/websocket/WebsocketServices";
import Lottie from "lottie-react";
// import MissingTransactionPscreenSlider from "modules/payment_operations/components/MissingTransactionPscreen";
import { userManagementApis } from "@services/apis/UserManagement";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { AppState } from "../../../redux/reducer";
import ConstantFunctions from "../../../utils/constants_functions";
import P2PTransactionContainer from "./P2PTransactionContainer";
import PrimePhpEurTransactionContainer from "./PrimePhpEurTransactionContainer";
import PrimeTransactionContainer from "./PrimeTransactionContainer";
import TransactionContainerSkeleton from "./TransactionContainerSkeleton";
import TransactionDetailContainer from "./TransactionDetailContainer";
import TransactionTypeSymbols from "./TransactionTypeSymbols";

interface TransactionDetailsProps {
  userId: number;
  merchantId: number;
  transactionID: number;
  ledger: string;
}

const TransactionsContainer: React.FC<TransactionDetailsProps> = ({
  userId,
  merchantId,
  transactionID,
  ledger,
}) => {
  const constantFunctions = new ConstantFunctions();
  const transactionStatusClass = new TransactionStatusClass();
  const [loading, setLoading] = useState<boolean>(true);
  const [isPrimeTransaction, setIsPrimeTransaction] = useState<boolean>(false);
  const [isPrimePhpEurTransaction, setIsPrimePhpEurTransaction] =
    useState<boolean>(false);
  const [isP2PTransaction, setIsP2PTransaction] = useState<boolean>(false);
  const userDetails = useSelector((state: AppState) => state.user);

  const [errorTransaction, setErrorTransaction] = useState<boolean>(false);
  const [transactionData, setTransactionData] =
    useState<TransactionByIdResponseData>();
  const currentMerchantId = useSelector(
    (state: { user: { currentMerchantId: number } }) =>
      state.user.currentMerchantId,
  );

  const [openMissingTransactionSlider, setOpenMissingTransactionSlider] =
    useState<boolean>(false);
  const [counterpartyPanelOpen, setCounterpartyPanelOpen] =
    useState<boolean>(false);
  const [travelRulePanelOpen, setTravelRulePanelOpen] =
    useState<boolean>(false);
  const [kycPanelOpen, setKycPanelOpen] = useState<boolean>(false);
  const [needsBeneficiaryDetails, setNeedsBeneficiaryDetails] =
    useState<boolean>(false);
  const [highRisk, setHighRisk] = useState<boolean>(false);

  const isMobile = useMediaQuery({ query: "(max-width: 640px)" });
  const displayValueOrDash = (value: any) => (value ? value.toString() : "--");

  const sourceMap = {
    invoice: "INVOICE",
    payment_button: "PAYMENT BUTTON",
    payment_form: "PAYMENT FORM",
    pos: "POS",
  };

  const [blockNumber, setBlockNumber] = useState<number>(0);
  const websocketLoaded = useSelector(
    (state: AppState) => state.websocketLoaded,
  );

  function getTransactionLoader(status: string): any {
    status = status.toLowerCase();
    if (
      status === "completed" ||
      status === "over payment" ||
      status === "refund success" ||
      status === "compliance" ||
      status === "under payment"
    ) {
      return SuccessTransactionLoader;
    } else if (
      status === "pending" ||
      status === "processing" ||
      status === "confirmation" ||
      status === "waiting" ||
      status === "initiated" ||
      status === "insufficient balance"
    ) {
      return InProgressTransactionLoader;
    } else if (
      status === "failed" ||
      status === "cancelled" ||
      status === "system error" ||
      status === "refund failed" ||
      status === "expired"
    ) {
      return FailureTransationLoader;
    }
  }

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await userManagementApis.getUserTransactionsDetails(
        userId,
        transactionID.toString(),
        merchantId,
      );
      setTransactionData(response.data);
      if (
        response.data.transactionType === "Merchant Escrow Payin" ||
        response.data.transactionType === "Merchant Escrow Payout" ||
        response.data.transactionType === "Merchant Escrow Payout Reversal" ||
        response.data.transactionType === "Merchant Escrow Payin Reversal" ||
        response.data.transactionType.startsWith("Prime ")
      ) {
        setIsPrimeTransaction(true);
      } else if (
        response.data.transactionType.startsWith("PHP") ||
        response.data.transactionType.startsWith("EUR")
      ) {
        setIsPrimePhpEurTransaction(true);
      } else {
        setIsPrimeTransaction(false);
        setIsPrimePhpEurTransaction(false);
      }
      if (
        response.data.transactionType === "Escrow Payout" ||
        response.data.transactionType === "Escrow Payin" ||
        response.data.transactionType === "Escrow Payin Reversal" ||
        response.data.transactionType === "Escrow Payout Reversal"
      ) {
        setIsP2PTransaction(true);
      } else {
        setIsP2PTransaction(false);
      }
    } catch (error) {
      setErrorTransaction(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (transactionData?.isFinal === 1) {
      setIsSettling(false);
    }
  }, [transactionData]);

  useEffect(() => {
    // First, fetch data from the API when the component is mounted
    fetchData();
    if (transactionID !== 0 && websocketLoaded) {
      // Set up WebSocket subscription after initial API data is fetched
      if (currentMerchantId !== -1) {
        webSocketService.onMessage(
          "merchant_transaction_update",
          true,
          (response: TransactionByIdResponseData) => {
            console.log(response);

            if (response.transactionId === transactionID) {
              setTransactionData(response);

              if (response.isFinal === 1) {
                setIsSettling(false);
              }
            }
          },
        );
      } else {
        webSocketService.onMessage(
          "personal_transaction_update",
          true,
          (response: TransactionByIdResponseData) => {
            console.log(response);
            if (response.transactionId === transactionID) {
              setTransactionData(response);

              if (response.isFinal === 1) {
                setIsSettling(false);
              }
            }
          },
        );
      }
    }
  }, [transactionID, websocketLoaded, currentMerchantId]);

  useEffect(() => {
    const handleMessageForBlockConfirmation = (blockNumber: number) => {
      setBlockNumber(blockNumber);
    };

    if (
      websocketLoaded &&
      transactionData?.transactions &&
      transactionData?.transactions.length
    ) {
      webSocketService.sendMessage(
        "subRoom",
        {
          roomname: "block_confirmations",
        },
        true,
      );

      webSocketService.onMessage(
        transactionData?.network?.toUpperCase() ?? "TPNK",
        true,
        handleMessageForBlockConfirmation,
      );
      return () => {
        webSocketService.offMessage(
          transactionData?.network?.toUpperCase() ?? "TPNK",
          true,
        );
      };
    }
  }, [transactionData, websocketLoaded]);

  const bgColor =
    transactionData?.status &&
    transactionStatusClass.getTransactionBgColor(transactionData.status);

  // const personalComplianceStatus = useMemo(
  //   () => getPersonalComplianceStatus(transactionData, currentMerchantId),
  //   [currentMerchantId, transactionData],
  // );

  // const complianceWalletAddress =
  //   ledger === "CR"
  //     ? (transactionData?.transactions?.[0]?.fromAddress?.trim() ?? "")
  //     : (transactionData?.toAddress?.trim() ?? "");

  // const shouldShowComplianceKycAction =
  //   personalComplianceStatus?.complianceType === "KYC_INCOMPLETE";

  // const complianceKycGateUidRef = useRef<number | null>(null);
  // const complianceKycGateFlowRef = useRef<TransactionKycFlowKey | null>(null);
  // const [complianceKycGateLoading, setComplianceKycGateLoading] =
  //   useState(false);
  // const [complianceKycCooldownMs, setComplianceKycCooldownMs] = useState(0);

  // const loadComplianceKycGate = useCallback(async () => {
  //   if (!shouldShowComplianceKycAction) return;
  //   try {
  //     const [profileRes, kycRes] = await Promise.all([
  //       profileService.getProfile(),
  //       kycServices.getKYCStatusV2(),
  //     ]);
  //     const data = (kycRes as { data?: unknown })?.data ?? kycRes;
  //     const isIndia = isIndiaCountryCode(profileRes.data.countryCode);
  //     const flow = getTransactionKycNextFlowKey(isIndia, data as any);
  //     const uid = profileRes.data.userId;
  //     complianceKycGateUidRef.current = uid;
  //     complianceKycGateFlowRef.current = flow;
  //     setComplianceKycCooldownMs(
  //       getTransactionKycCooldownRemainingMs(uid, flow),
  //     );
  //   } catch {
  //     complianceKycGateUidRef.current = null;
  //     complianceKycGateFlowRef.current = null;
  //     setComplianceKycCooldownMs(0);
  //   }
  // }, [shouldShowComplianceKycAction]);

  // useEffect(() => {
  //   if (!shouldShowComplianceKycAction) {
  //     complianceKycGateUidRef.current = null;
  //     complianceKycGateFlowRef.current = null;
  //     setComplianceKycCooldownMs(0);
  //     setComplianceKycGateLoading(false);
  //     return;
  //   }
  //   setComplianceKycGateLoading(true);
  //   loadComplianceKycGate().finally(() => setComplianceKycGateLoading(false));
  // }, [
  //   shouldShowComplianceKycAction,
  //   transactionData?.transactionId,
  //   loadComplianceKycGate,
  // ]);

  // useEffect(() => {
  //   if (!shouldShowComplianceKycAction) return;
  //   const id = window.setInterval(() => {
  //     const uid = complianceKycGateUidRef.current;
  //     const flow = complianceKycGateFlowRef.current;
  //     if (uid == null || flow == null) return;
  //     setComplianceKycCooldownMs(
  //       getTransactionKycCooldownRemainingMs(uid, flow),
  //     );
  //   }, 1000);
  //   return () => clearInterval(id);
  // }, [shouldShowComplianceKycAction]);

  // const prevKycPanelOpenRef = useRef(kycPanelOpen);
  // useEffect(() => {
  //   const prev = prevKycPanelOpenRef.current;
  //   prevKycPanelOpenRef.current = kycPanelOpen;
  //   if (
  //     prev === true &&
  //     kycPanelOpen === false &&
  //     shouldShowComplianceKycAction
  //   ) {
  //     void loadComplianceKycGate();
  //   }
  // }, [kycPanelOpen, shouldShowComplianceKycAction, loadComplianceKycGate]);

  // const shouldShowComplianceDetailsAction = needsBeneficiaryDetails;

  // const complianceDetailsActionLabel =
  //   personalComplianceStatus?.missingDetailsKind === "originator"
  //     ? "Provide originator details"
  //     : "Provide beneficiary details";

  // const complianceToneStyles = personalComplianceStatus
  //   ? COMPLIANCE_TONE_STYLES[personalComplianceStatus.tone]
  //   : COMPLIANCE_TONE_STYLES.neutral;

  // useEffect(() => {
  //   if (!complianceWalletAddress) {
  //     setNeedsBeneficiaryDetails(false);
  //     setHighRisk(false);
  //     return;
  //   }

  //   transactionService
  //     .checkWithdrawal({ walletAddress: complianceWalletAddress })
  //     .then((res) => {
  //       setNeedsBeneficiaryDetails(res.walletExists === false);
  //       setHighRisk((res?.highRisk ?? 0) === 1);
  //     })
  //     .catch(() => {
  //       setNeedsBeneficiaryDetails(false);
  //       setHighRisk(false);
  //     });
  // }, [complianceWalletAddress, currentMerchantId]);

  // const [isSettleTransactionButtonLoading, setIsTransactionButtonLoading] =
  //   useState<boolean>(false);
  // const [isSendCallBackButtonLoading, setIsSendCallBackButtonLoading] =
  //   useState<boolean>(false);
  const [isSettling, setIsSettling] = useState<boolean>(false);
  // const { handleError, handleSuccess } = useSnackbarMessage();

  // const handleSettleTransaction = async () => {
  //   try {
  //     setIsTransactionButtonLoading(true);
  //     const settleTransactionRequest: SettleTransactionRequest = {
  //       transactionId: transactionData?.transactionId ?? 0,
  //     };

  //     const response: SettleTransactionResponse =
  //       await transactionService.settleTransaction(settleTransactionRequest);
  //     setIsSettling(true);
  //     handleSuccess({
  //       message: response.msg || "Transaction settled successfully",
  //     });
  //   } catch (error: any) {
  //     const errorMessage =
  //       error?.response?.data?.msg ||
  //       "Updating status failed. Please try again.";
  //     handleError({ message: errorMessage });
  //   } finally {
  //     setIsTransactionButtonLoading(false);
  //   }
  // };

  // const handleSendCallBack = async () => {
  //   try {
  //     setIsSendCallBackButtonLoading(true);
  //     const settleTransactionRequest: SettleTransactionRequest = {
  //       transactionId: transactionData?.transactionId ?? 0,
  //     };

  //     const response: SettleTransactionResponse =
  //       await transactionService.settleTransaction(
  //         settleTransactionRequest,
  //         "/transactions/sendCPGWebhook",
  //       );
  //     handleSuccess({
  //       message: response.msg || "Transaction callback sent successfully",
  //     });
  //   } catch (error: any) {
  //     const errorMessage =
  //       error?.response?.data?.msg ||
  //       "Sending callback failed. Please try again.";
  //     handleError({ message: errorMessage });
  //   } finally {
  //     setIsSendCallBackButtonLoading(false);
  //   }
  // };

  if (errorTransaction) {
    return <GenericError />;
  } else if (!loading) {
    return (
      <div className="flex flex-col relative">
        <div className="sticky top-0 z-50 flex flex-col bg-bg-page-body pb-6">
          <div
            className={`flex flex-row items-center justify-center rounded-lg py-[4px] h-[50px] mx-[20px] ${
              isMobile ? "mt-[16px]" : ""
            } ${bgColor}`}>
            <div className="max-h-[48px] max-w-[48px]">
              <Lottie
                loop={true}
                autoplay={true}
                animationData={getTransactionLoader(
                  transactionData?.status ?? "",
                )}
                rendererSettings={{
                  preserveAspectRatio: "xMidYMid slice",
                }}
              />
            </div>
            <span className="text-center text-text-base text-[16px] font-medium">
              {transactionStatusClass.getTransactionHeader(
                transactionData?.status ?? "",
              )}
            </span>
          </div>
        </div>

        <div
          className={`flex flex-col ${
            isPrimeTransaction ||
            !(
              transactionData?.transactions &&
              transactionData.transactions.length > 0
            )
              ? "mb-[20px]"
              : ""
          }`}>
          {/* Transaction Content*/}
          {isPrimeTransaction ? (
            <PrimeTransactionContainer transactionData={transactionData!} />
          ) : isP2PTransaction ? (
            <P2PTransactionContainer transactionData={transactionData!} />
          ) : isPrimePhpEurTransaction ? (
            <PrimePhpEurTransactionContainer
              transactionData={transactionData!}
            />
          ) : (
            <div className="flex flex-col gap-[20px]">
              <div className="border border-bg-header overflow-scroll flex flex-col rounded-lg mx-[20px]">
                <div className="border-b border-b-bg-header px-[16px] py-[18px] flex flex-row justify-between">
                  <div className="flex flex-row">
                    <TransactionTypeSymbols
                      transactionType={transactionData?.transactionType ?? ""}
                      ledger={transactionData?.ledger ?? ""}
                    />
                    <span className="text-[16px] font-medium text-text-base">
                      {transactionData?.ledger === "CR"
                        ? `${
                            transactionData?.isFinal === 1
                              ? "Received"
                              : "Receiving"
                          }`
                        : `${
                            transactionData?.isFinal === 1 ? "Sent" : "Sending"
                          }`}
                    </span>
                  </div>
                  <div className="flex flex-row items-center gap-[4px]">
                    <span className="text-[16px] font-bold text-text-base">
                      {constantFunctions.formatAmount(
                        Number(
                          transactionData?.ledger === "CR"
                            ? transactionData?.isFinal === 1
                              ? transactionData?.settledAmountReceived
                              : transactionData?.settledAmountRequested
                            : transactionData?.isFinal === 1
                              ? transactionData?.settledAmountSent
                              : transactionData?.settledAmountRequested,
                        ) ?? "",
                      )}
                    </span>
                    <img
                      src={transactionData?.settledCurrencyImage}
                      width={18}
                      height={18}
                    />
                  </div>
                </div>
                {/* <div className="flex flex-col py-[16px] gap-[16px]">
                  <StatusMessage
                    status={transactionData?.status.toLowerCase()}
                  />
                  {personalComplianceStatus &&
                    userDetails.accountType !== undefined &&
                    userDetails.accountType !== "institutional" && (
                      <div className="px-[16px]">
                        <div className="rounded-lg border border-border-subtle bg-bg-page-body p-[12px] flex flex-col gap-[8px]">
                          <div className="flex items-center gap-[8px]">
                            <span
                              className={`material-symbols-outlined text-[18px] ${complianceToneStyles.icon}`}>
                              {complianceToneStyles.iconName}
                            </span>
                            <div className="flex flex-col gap-[6px]">
                              <div className="flex flex-wrap items-center gap-[8px]">
                                <span className="text-[12px] font-medium text-text-base">
                                  Compliance
                                </span>
                                <span
                                  className={`rounded-full px-[8px] py-[4px] text-[11px] font-semibold ${complianceToneStyles.badge}`}>
                                  {personalComplianceStatus.complianceLabel}
                                </span>
                              </div>
                              <p className="text-[12px] text-text-subtle">
                                {personalComplianceStatus.helperText}
                              </p>
                              {shouldShowComplianceKycAction &&
                                (complianceKycGateLoading ? (
                                  <span className="text-[12px] text-text-subtle">
                                    Checking KYC…
                                  </span>
                                ) : complianceKycCooldownMs > 0 ? (
                                  <span className="text-[12px] text-text-subtle font-medium">
                                    {formatTransactionKycTryAgainIn(
                                      complianceKycCooldownMs,
                                    )}
                                  </span>
                                ) : (
                                  <span
                                    className="text-text-navbar-active text-[12px] font-medium cursor-pointer"
                                    onClick={() => setKycPanelOpen(true)}>
                                    Complete KYC
                                  </span>
                                ))}
                              {shouldShowComplianceDetailsAction && (
                                <span
                                  className="text-text-navbar-active text-[12px] font-medium cursor-pointer"
                                  onClick={() =>
                                    setCounterpartyPanelOpen(true)
                                  }>
                                  {complianceDetailsActionLabel}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  {userDetails.accountType !== undefined &&
                    userDetails.accountType !== "institutional" && (
                      <div className="flex flex-row justify-between items-center mx-4">
                        <span className="font-medium text-[12px] w-[40%] text-text-label">
                          {ledger === "CR"
                            ? "Originator details"
                            : "Beneficiary details"}
                        </span>
                        <span
                          className="text-text-navbar-active text-[12px] font-medium cursor-pointer text-right"
                          onClick={() => setTravelRulePanelOpen(true)}>
                          {ledger === "CR"
                            ? "View Originator details"
                            : "View Beneficiary details"}
                        </span>
                      </div>
                    )}
                  <div className="flex flex-col space-y-[16px] px-[16px]">
                    <LabelRow
                      label={"Transaction Type"}
                      value={displayValueOrDash(
                        getTransactionTypeText(
                          transactionData?.transactionType ?? "",
                          transactionData?.internalType ?? "",
                        ),
                      )}
                    />
                    {sourceMap[transactionData?.source ?? ""] && (
                      <LabelRow
                        label={"Source"}
                        valueComp={
                          <span
                            className={`text-[12px] font-medium rounded-sm px-[6px] py-[4px] text-text-base bg-bg-base`}
                            style={{
                              display: "inline-block",
                              whiteSpace: "nowrap",
                            }}>
                            {displayValueOrDash(
                              sourceMap[
                                transactionData?.source ?? ""
                              ].toUpperCase(),
                            )}
                          </span>
                        }
                      />
                    )}
                    <LabelRow
                      label={"Date & Time"}
                      value={`${constantFunctions.formatDateTime(
                        transactionData?.updatedAt ??
                          transactionData?.createdAt ??
                          "",
                      )}`}
                    />
                  </div>
                  <div className="border-t border-border-subtle"></div>
                  <div className="flex flex-col space-y-[16px] px-[16px]">
                    <div className="flex flex-row items-center gap-[4px]">
                      <InfoIcon />
                      <span className="text-[14px] font-normal text-text-base">
                        Payment Details
                      </span>
                    </div>
                    <LabelRow
                      label="Base Currency"
                      valueComp={
                        <div className="flex flex-row items-center gap-[4px]">
                          <img
                            src={transactionData?.baseCurrencyImage}
                            height={16}
                            width={16}
                          />
                          <p className="text-text-base text-[12px] font-medium">
                            {transactionData?.baseCurrency}
                          </p>
                        </div>
                      }
                    />
                    <LabelRow
                      label="Settled Currency"
                      valueComp={
                        <div className="flex flex-row items-center gap-[4px]">
                          <img
                            src={transactionData?.settledCurrencyImage}
                            height={16}
                            width={16}
                          />
                          <p className="text-text-base text-[12px] font-medium">
                            {transactionData?.settledCurrency}
                          </p>
                        </div>
                      }
                    />
                    <LabelRow
                      label="Network"
                      valueComp={
                        <div className="flex flex-row items-center gap-[4px]">
                          {transactionData?.network !== "TPNK" && (
                            <img
                              src={transactionData?.networkImage}
                              height={16}
                              width={16}
                            />
                          )}
                          <p className="text-text-base text-[12px] font-medium">
                            {transactionData?.network}
                          </p>
                        </div>
                      }
                    />
                    {transactionData?.toFrom && (
                      <LabelRow
                        label={`${
                          ledger === "CR" ? "Received from" : "Sent to"
                        }`}
                        value={transactionData?.toFrom}
                        isCopy={
                          transactionData?.internalType !== "Self"
                            ? true
                            : false
                        }
                      />
                    )}
                    {(transactionData?.transactionType === "Personal Payin" ||
                      transactionData?.transactionType === "Personal Payout" ||
                      transactionData?.transactionType === "Merchant Payin" ||
                      transactionData?.transactionType ===
                        "Merchant Payout") && (
                      <LabelRow
                        label={`${
                          ledger === "CR" ? "Received from" : "Sent to"
                        }`}
                        value={
                          ledger === "CR"
                            ? transactionData?.transactions &&
                              transactionData.transactions.length > 0 &&
                              transactionData.transactions[0].fromAddress &&
                              transactionData.transactions[0].fromAddress.trim() !==
                                ""
                              ? transactionData.transactions[0].fromAddress
                                  .length > 20
                                ? `${transactionData.transactions[0].fromAddress.substring(
                                    0,
                                    10,
                                  )}...${transactionData.transactions[0].fromAddress.substring(
                                    transactionData.transactions[0].fromAddress
                                      .length - 10,
                                  )}`
                                : transactionData.transactions[0].fromAddress
                              : "--"
                            : transactionData?.toAddress &&
                                transactionData.toAddress.trim() !== ""
                              ? transactionData.toAddress.length > 20
                                ? `${transactionData.toAddress.substring(
                                    0,
                                    10,
                                  )}...${transactionData.toAddress.substring(
                                    transactionData.toAddress.length - 10,
                                  )}`
                                : transactionData.toAddress
                              : "--"
                        }
                        isCopy={
                          (
                            ledger === "CR"
                              ? transactionData?.transactions?.[0]?.fromAddress
                              : transactionData?.toAddress
                          )
                            ? true
                            : false
                        }
                        copyValue={
                          ledger === "CR"
                            ? transactionData?.transactions?.[0]?.fromAddress
                            : transactionData?.toAddress
                        }
                      />
                    )}
                    <LabelRow
                      label="Order ID"
                      value={transactionData?.orderId}
                      isCopy={true}
                    />
                    {transactionData?.transactionType === "Merchant Payin" &&
                      transactionData.orderId && (
                        <LabelRow
                          label={"Payment Link"}
                          valueComp={
                            <div className="flex gap-[4px] items-center">
                              <span
                                className={`text-text-navbar-active text-[12px] font-medium cursor-pointer truncate ${
                                  isMobile ? "max-w-[180px]" : ""
                                }`}
                                onClick={() => {
                                  const baseUrl = window.location.origin;
                                  const paymentUrl = `${baseUrl}/pscreen/${transactionData.orderId}`;
                                  window.open(paymentUrl, "_blank");
                                }}>
                                {`${
                                  window.location.origin
                                }/pscreen/${transactionData.orderId.substring(
                                  0,
                                  12,
                                )}...`}
                              </span>
                              <span
                                className="material-symbols-outlined text-[14px] text-text-navbar-active align-middle cursor-pointer"
                                onClick={() => {
                                  const baseUrl = window.location.origin;
                                  const paymentUrl = `${baseUrl}/pscreen/${transactionData.orderId}`;
                                  window.open(paymentUrl, "_blank");
                                }}>
                                open_in_new
                              </span>
                              <CopyButton
                                copyText={`${window.location.origin}/pscreen/${transactionData.orderId}`}
                                copyMessage="Payment link copied to clipboard"
                                fillColor="bg-border-button-secondary"
                              />
                            </div>
                          }
                        />
                      )}
                    <div className="flex flex-col gap-[4px]">
                      <span className="text-text-label font-medium text-[12px]">
                        Notes
                      </span>
                      <span className="text-text-base font-medium text-[12px]">
                        {transactionData?.comments !== ""
                          ? transactionData?.comments
                          : transactionData?.systemComments !== ""
                            ? transactionData?.systemComments
                            : "- -"}
                      </span>
                    </div>
                  </div>
                  <div className="border-t border-border-subtle"></div>
                  <div className="flex flex-col space-y-[16px] px-[16px]">
                    {currentMerchantId === -1 &&
                    transactionData?.ledger === "CR" ? null : (
                      <LabelRow
                        label={"Requested"}
                        value={`${constantFunctions.formatAmount(
                          transactionData?.settledAmountRequested ?? "",
                        )}${" "}
                      ${transactionData?.settledCurrency}`}
                      />
                    )}
                    <LabelRow
                      label={
                        transactionData?.ledger === "CR"
                          ? "Received"
                          : "Debited"
                      }
                      value={`${
                        transactionData?.isFinal !== 1
                          ? "0.00"
                          : constantFunctions.formatAmount(
                              transactionData?.transactionType
                                .substring(0, 8)
                                .toLowerCase() === "internal"
                                ? transactionData.settledAmountRequested
                                : transactionData?.ledger === "CR"
                                  ? (transactionData?.settledAmountReceived ??
                                    "")
                                  : (transactionData?.settledAmountDebited ??
                                    ""),
                            )
                      }${" "}
                      ${transactionData?.settledCurrency}`}
                    />
                    {transactionData?.ledger === "CR" && (
                      <LabelRow
                        label="Received in base"
                        value={`${constantFunctions.formatAmount(
                          transactionData?.baseAmountReceived ?? 0,
                        )} ${transactionData?.baseCurrency}`}
                      />
                    )}
                    <LabelRow
                      label={"Commission"}
                      value={`${
                        transactionData?.isFinal === 1 &&
                        (transactionData?.ledger === "CR"
                          ? transactionData?.settledAmountReceived === 0
                          : transactionData?.settledAmountDebited === 0)
                          ? "0.00"
                          : constantFunctions.formatAmount(
                              transactionData?.commission ?? "",
                            )
                      }${" "}
                  ${transactionData?.settledCurrency}`}
                    />
                    <LabelRow
                      label={
                        transactionData?.ledger === "CR" ? "Credited" : "Sent"
                      }
                      value={`${
                        transactionData?.isFinal !== 1
                          ? "0.00"
                          : constantFunctions.formatAmount(
                              transactionData?.transactionType
                                .substring(0, 8)
                                .toLowerCase() === "internal"
                                ? transactionData.settledAmountRequested
                                : transactionData?.ledger === "CR"
                                  ? (transactionData?.settledAmountCredited ??
                                    "")
                                  : (transactionData?.settledAmountSent ?? ""),
                            )
                      }${" "}${transactionData?.settledCurrency}`}
                    />
                  </div>

                  <div className="flex flex-row items-center justify-between px-[16px]">
                    {currentMerchantId !== -1 ? (
                      <div className="flex justify-between w-full">
                        {transactionData?.transactionType ===
                          "Merchant Payin" && (
                          <CustomButton
                            text={isSettling ? "SETTLING" : "SETTLE"}
                            isActive={
                              !isSettling &&
                              (transactionData?.settledAmountReceived ?? 0) >
                                0 &&
                              (transactionData?.settledAmountReceived ?? 0) <
                                (transactionData?.settledAmountRequested ??
                                  0) &&
                              transactionData?.isFinal === 0
                            }
                            textSize="text-[12px]"
                            onClick={() => handleSettleTransaction()}
                            isButtonLoading={isSettleTransactionButtonLoading}
                          />
                        )}
                        <CustomButton
                          text="SEND CALLBACK"
                          isActive={
                            transactionData?.transactionType ===
                              "Merchant Payin" ||
                            transactionData?.transactionType ===
                              "Merchant Payout"
                          }
                          textSize="text-[12px]"
                          onClick={() => handleSendCallBack()}
                          isButtonLoading={isSendCallBackButtonLoading}
                        />

                        {transactionData?.transactionType ===
                          "Merchant Payin" &&
                          transactionData.status.toLowerCase() !==
                            "expired" && (
                            <CustomButton
                              text={"MISSING TRANSACTION?"}
                              isActive={true}
                              textSize="text-[12px]"
                              onClick={() =>
                                setOpenMissingTransactionSlider(true)
                              }
                            />
                          )}
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                </div> */}
              </div>
              {transactionData?.transactions &&
                transactionData.transactions.length > 0 && (
                  <div className="bg-bg-base p-[20px] flex flex-col">
                    <span className="font-semibold text-[12px] text-text-base">
                      Transaction details
                    </span>
                    <div className="overflow-scroll">
                      {transactionData.transactions.map(
                        (transactionDetail, index) => (
                          <TransactionDetailContainer
                            key={index}
                            transaction={transactionDetail}
                            network={`${transactionData.network}`}
                            blockNumberFromSocket={blockNumber}
                            index={index}
                            ledger={ledger}
                          />
                        ),
                      )}
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>
        {/* <MissingTransactionPscreenSlider
          onClose={() =>
            setOpenMissingTransactionSlider(!openMissingTransactionSlider)
          }
          isOpen={openMissingTransactionSlider}
          network={transactionData?.network || ""}
          txnOrderId={transactionData?.orderId || ""}
        />
        <TravelRuleCounterpartyPanel
          isOpen={travelRulePanelOpen}
          onClose={() => setTravelRulePanelOpen(false)}
          transactionId={transactionID}
          ledger={ledger}
        />
        <CounterpartyDetailsPanel
          isOpen={counterpartyPanelOpen}
          onClose={() => setCounterpartyPanelOpen(false)}
          walletAddress={complianceWalletAddress}
          ledger={ledger}
          onSuccess={() => {
            setCounterpartyPanelOpen(false);
            fetchData();
            setNeedsBeneficiaryDetails(false);
            setHighRisk(false);
          }}
          highRisk={highRisk}
          transactionValue={
            transactionData?.settledAmountRequested ??
            transactionData?.baseAmount ??
            0
          }
        />
        <TransactionKycPanel
          isOpen={kycPanelOpen}
          onClose={() => setKycPanelOpen(false)}
          onSuccess={() => {
            setKycPanelOpen(false);
            fetchData();
          }}
        /> */}
      </div>
    );
  } else {
    return <TransactionContainerSkeleton />;
  }
};

export default TransactionsContainer;
